/**
 * Event-related utility functions
 */

/**
 * Get human-readable label for event category
 * @param {string} category - Event category code
 * @returns {string} Human-readable category label
 */
export const getCategoryLabel = (category) => {
  const labels = {
    'pinball': 'Pinball',
    'video_game': 'Video Game',
    'board_game': 'Board Game',
    'book_club': 'Book Club',
    'other': 'Other'
  };
  return labels[category] || 'Other';
};

/**
 * Get available event categories
 * @returns {Array} Array of category objects with value and label
 */
export const getEventCategories = () => [
  { value: 'pinball', label: 'Pinball' },
  { value: 'video_game', label: 'Video Game' },
  { value: 'board_game', label: 'Board Game' },
  { value: 'book_club', label: 'Book Club' },
  { value: 'other', label: 'Other' }
];

/**
 * Map an event category to a design system badge variant.
 *
 * This exists as ONE exported map because it previously existed as two. EventsHub styled
 * the categories through `.category-*` CSS rules while EventDetails carried its own
 * `getCategoryBadgeVariant` helper, and the two had drifted apart: board_game was purple
 * on one page and amber on the other, book_club amber on one and green on the other.
 * Converging the colours without converging the source would just restart that clock.
 *
 * The values here follow EventsHub, which had four of the five call sites.
 *
 * @param {string} category - Event category code
 * @returns {string} DSBadge variant name
 */
export const getCategoryVariant = (category) => {
  const variants = {
    'pinball': 'error',
    'video_game': 'info',
    'board_game': 'brand',
    'book_club': 'warning',
    'other': 'neutral'
  };
  return variants[category] || 'neutral';
};

/**
 * What the server falls back to when an event carries no duration of its own. Kept here
 * because three screens were each hardcoding their own 240.
 */
export const DEFAULT_EVENT_DURATION_MINUTES = 240;

/** When an event finishes: its start plus its duration. Null if it has no usable date. */
export const eventEndTime = (event) => {
  const start = new Date(event?.event_date || '').getTime();
  if (Number.isNaN(start)) return null;
  const minutes = Number(event?.duration_minutes) || DEFAULT_EVENT_DURATION_MINUTES;
  return start + minutes * 60 * 1000;
};

// The forms take an end time rather than a duration. Both work on the start's wall clock
// ("YYYY-MM-DDTHH:MM", as a datetime-local input gives it). An end at or before the start
// is taken as the next day, so a night can run past midnight.
const clockMinutes = (value) => {
  const match = /(\d{1,2}):(\d{2})$/.exec(String(value || '').slice(0, 16));
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

/** The "HH:MM" an event starting at startLocal and running this long ends at. */
export const endTimeInputValue = (startLocal, durationMinutes) => {
  const start = clockMinutes(startLocal);
  if (start === null) return '';
  const end = (start + (Number(durationMinutes) || DEFAULT_EVENT_DURATION_MINUTES)) % (24 * 60);
  return `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
};

/** How long an event runs from startLocal to an "HH:MM" end, or null without both. */
export const durationFromEndTime = (startLocal, endTime) => {
  const start = clockMinutes(startLocal);
  const end = clockMinutes(endTime);
  if (start === null || end === null) return null;
  const minutes = end - start;
  return minutes > 0 ? minutes : minutes + 24 * 60;
};

/**
 * The moment an event stops being current.
 *
 * An event used to drop out of the list the second its start time passed, which is exactly
 * when a host needs to reach it - to check people in, or to send an update to the room. So
 * it stays until it is over, and then until the end of the day it started on, which covers
 * the hour after the doors close without anyone having to get the duration exactly right.
 * A night that runs past midnight keeps its own end time, since that is the later of the two.
 */
export const eventVisibleUntil = (event) => {
  const end = eventEndTime(event);
  if (end === null) return null;
  const endOfDay = new Date(event.event_date);
  endOfDay.setHours(23, 59, 59, 999);
  return Math.max(end, endOfDay.getTime());
};

/** True while an event is under way: started, not yet finished. */
export const isEventInProgress = (event, now = Date.now()) => {
  const end = eventEndTime(event);
  if (end === null) return false;
  return now >= new Date(event.event_date).getTime() && now <= end;
};

/**
 * True once an event is done with for good. This, not the start time, is what separates
 * upcoming from past: an event in progress belongs with the ones still to come.
 */
export const isEventOver = (event, now = Date.now()) => {
  const until = eventVisibleUntil(event);
  return until === null ? false : now > until;
};

/**
 * True when an admin has called the event off (Series dates, Cancel). A cancelled event
 * stays listed on purpose, so anyone who had it in mind sees the news, but every screen
 * has to say so plainly and stop offering RSVP, check-in or cards.
 *
 * This is the event-level cancellation, not PinGolf's own `event_metadata.pingolf.canceled_at`,
 * which only stops card sales.
 */
export const isEventCancelled = (event) => Boolean(event?.cancelled_at);
